// Utils
import { hasCommandModifier, isControlKeyCommand } from '../../utils/keyboard/keyboardUtility';
import { isPlatformApple } from '../../platform/utils/platformDetailsUtils';

// Constants
import { ZOOM_PROPS } from './zoomConstants';
import { DeviceFormFactor, PlatformDetails } from '../../../common/platform/platformTypes';

/** Whether the current wheel gesture is scrolling, zooming, or idle. */
export const WHEEL_ZOOM_MODES = {
    NONE: 'NONE',
    SCROLL: 'SCROLL',
    ZOOM: 'ZOOM',
} as const;

export type WheelZoomMode = (typeof WHEEL_ZOOM_MODES)[keyof typeof WHEEL_ZOOM_MODES];

/** How long after the last wheel event the mode resets to idle, so inertia tails stay attributed to the gesture that produced them. */
const DEFAULT_WHEEL_MODE_RESET_DELAY_MS = 150;

/**
 * Whether a wheel event should zoom rather than scroll.
 * Matches cmd/ctrl + wheel and Mac trackpad pinch (ctrlKey without metaKey).
 */
export const isWheelZoomScroll = (event: Pick<WheelEvent, 'ctrlKey' | 'metaKey'>): boolean =>
    hasCommandModifier(event) || isControlKeyCommand(event);

/**
 * Detects Mac desktop trackpad pinch zoom, which the OS reports as ctrlKey wheel events.
 */
export const isMacOsTrackpadPinchStyleZoomWheel = (
    event: Pick<WheelEvent, 'ctrlKey' | 'metaKey'>,
    platformDetails: PlatformDetails,
): boolean =>
    isPlatformApple(platformDetails) &&
    platformDetails.formFactor === DeviceFormFactor.desktop &&
    !!event.ctrlKey &&
    !event.metaKey;

/**
 * Returns whether the wheel event is a pinch-style zoom gesture.
 */
export const isPinchStyleZoomWheel = (
    event: Pick<WheelEvent, 'ctrlKey' | 'metaKey'>,
    { platformDetails }: { platformDetails: PlatformDetails },
): boolean => isWheelZoomScroll(event) && isMacOsTrackpadPinchStyleZoomWheel(event, platformDetails);

/**
 * Normalises wheel deltaY for zoom, applying the pinch multiplier when appropriate.
 */
export const getWheelZoomDeltaY = (
    event: Pick<WheelEvent, 'deltaY' | 'ctrlKey' | 'metaKey'>,
    { platformDetails }: { platformDetails: PlatformDetails },
): number => {
    const isPinchStyleWheel = isPinchStyleZoomWheel(event, { platformDetails });

    return isPinchStyleWheel ? event.deltaY * ZOOM_PROPS.TRACKPAD_PINCH_STYLE_ZOOM_DELTA_MULTIPLIER : event.deltaY;
};

/**
 * Tracks whether the current wheel gesture is scrolling, zooming, or idle.
 * Used to ignore inertia tails when switching between scroll and zoom modes.
 */
export const createWheelModeTracker = ({
    resetDelayMs = DEFAULT_WHEEL_MODE_RESET_DELAY_MS,
}: {
    resetDelayMs?: number;
} = {}) => {
    let currentMode: WheelZoomMode = WHEEL_ZOOM_MODES.NONE;
    let resetTimeoutId: number | null = null;

    const clearScheduledReset = () => {
        if (resetTimeoutId !== null) {
            window.clearTimeout(resetTimeoutId);
            resetTimeoutId = null;
        }
    };

    const scheduleReset = () => {
        if (resetDelayMs <= 0) return;

        clearScheduledReset();
        resetTimeoutId = window.setTimeout(() => {
            currentMode = WHEEL_ZOOM_MODES.NONE;
            resetTimeoutId = null;
        }, resetDelayMs);
    };

    return {
        getMode: (): WheelZoomMode => currentMode,
        setMode: (mode: WheelZoomMode) => {
            currentMode = mode;
        },
        scheduleReset,
        shouldIgnoreZoomInertia: (isInertiaScroll: boolean): boolean =>
            isInertiaScroll && currentMode !== WHEEL_ZOOM_MODES.ZOOM,
        shouldIgnoreScrollInertia: (isInertiaScroll: boolean): boolean =>
            isInertiaScroll && currentMode !== WHEEL_ZOOM_MODES.SCROLL,
        dispose: () => {
            clearScheduledReset();
            currentMode = WHEEL_ZOOM_MODES.NONE;
        },
    };
};
